import api from './api';

export const PRODUCT_OPTIONS = 'options';
export const PRODUCT_TRADES = 'trades';

const INTENT_STORAGE_KEY = 'geni_pending_product_intent';
const ACQUISITION_STORAGE_KEY = 'geni_pending_product_acquisition';
const OAUTH_ROUTING_STORAGE_KEY = 'geni_oauth_routing';

const isBrowser = typeof window !== 'undefined';

const readStorageValue = (key) => {
  if (!isBrowser) return null;
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
};

const writeStorageValue = (key, value) => {
  if (!isBrowser) return;
  if (!value) {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
    return;
  }
  window.sessionStorage.setItem(key, value);
  window.localStorage.setItem(key, value);
};

const removeStorageValue = (key) => {
  if (!isBrowser) return;
  window.sessionStorage.removeItem(key);
  window.localStorage.removeItem(key);
};

export const normalizeProductValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === PRODUCT_OPTIONS || normalized === PRODUCT_TRADES ? normalized : null;
};

export const getRedirectDestination = (searchParams, locationState) => {
  const redirectParam = searchParams.get('redirect');
  if (redirectParam) {
    try {
      const decoded = decodeURIComponent(redirectParam);
      if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        return decoded;
      }
    } catch (error) {
      console.warn('Invalid redirect param:', redirectParam, error);
    }
  }

  if (locationState?.from?.pathname) {
    const fromPath = locationState.from.pathname;
    const fromSearch = locationState.from.search || '';
    return fromPath + fromSearch;
  }

  return '/dashboard';
};

export const buildAuthPageUrl = (path, redirectDestination, product) => {
  const params = new URLSearchParams();
  if (redirectDestination) {
    params.set('redirect', redirectDestination);
  }
  const normalizedProduct = normalizeProductValue(product);
  if (normalizedProduct) {
    params.set('product', normalizedProduct);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const setPendingProductIntent = (product) => {
  writeStorageValue(INTENT_STORAGE_KEY, normalizeProductValue(product));
};

export const getPendingProductIntent = () => normalizeProductValue(readStorageValue(INTENT_STORAGE_KEY));

export const clearPendingProductIntent = () => removeStorageValue(INTENT_STORAGE_KEY);

export const setPendingAcquisitionProduct = (product) => {
  writeStorageValue(ACQUISITION_STORAGE_KEY, normalizeProductValue(product));
};

export const getPendingAcquisitionProduct = () => normalizeProductValue(readStorageValue(ACQUISITION_STORAGE_KEY));

export const clearPendingAcquisitionProduct = () => removeStorageValue(ACQUISITION_STORAGE_KEY);

export const setOAuthRoutingState = (payload) => {
  if (!isBrowser) return;
  if (!payload) {
    removeStorageValue(OAUTH_ROUTING_STORAGE_KEY);
    return;
  }
  const safePayload = JSON.stringify({
    redirectTo: payload.redirectTo || '/dashboard',
    allowAcquisition: Boolean(payload.allowAcquisition),
  });
  window.sessionStorage.setItem(OAUTH_ROUTING_STORAGE_KEY, safePayload);
};

export const getOAuthRoutingState = () => {
  if (!isBrowser) return null;
  const raw = window.sessionStorage.getItem(OAUTH_ROUTING_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(OAUTH_ROUTING_STORAGE_KEY);
  try {
    const parsed = JSON.parse(raw);
    return {
      redirectTo: parsed?.redirectTo || '/dashboard',
      allowAcquisition: Boolean(parsed?.allowAcquisition),
    };
  } catch (error) {
    console.warn('Invalid OAuth routing payload:', error);
    return null;
  }
};

export const resolvePostAuthDestination = async ({ requestedRedirect = '/dashboard', allowAcquisition = false } = {}) => {
  const requestedIntent = getPendingProductIntent();
  const acquisitionProduct = getPendingAcquisitionProduct();

  const response = await api.post('/user/product-routing/resolve', {
    requested_intent: requestedIntent,
    requested_redirect: requestedRedirect,
    allow_acquisition: allowAcquisition && acquisitionProduct === requestedIntent,
  });

  if (response.data?.intent_consumed) {
    clearPendingProductIntent();
    if (allowAcquisition && acquisitionProduct === response.data.intent_consumed) {
      clearPendingAcquisitionProduct();
    }
  }

  return response.data?.destination || requestedRedirect || '/dashboard';
};